import React from 'react'
import type { GroupResult } from '../../types'
import { getUIStrings } from '../../i18n/ui'
import { resolveColor } from '../../styles/colorContrast'
import { totalPoints, formatPoints } from '../../utils/score'

interface PyramidVisualizationProps {
  groups: GroupResult[]
  overallPercentage: number
  compact?: boolean
  lang?: string
}

const COLOR_MAP: Record<string, { hex: string; gradient: [string, string] }> = {
  red:    { hex: '#EF4444', gradient: ['#F87171', '#DC2626'] },
  pink:   { hex: '#EC4899', gradient: ['#F472B6', '#DB2777'] },
  orange: { hex: '#F97316', gradient: ['#FB923C', '#EA580C'] },
  yellow: { hex: '#F59E0B', gradient: ['#FCD34D', '#D97706'] },
  green:  { hex: '#10B981', gradient: ['#34D399', '#059669'] },
  blue:   { hex: '#3B82F6', gradient: ['#60A5FA', '#2563EB'] },
  purple: { hex: '#8B5CF6', gradient: ['#A78BFA', '#7C3AED'] },
}

function getColor(name: string): { hex: string; gradient: [string, string] } {
  if (COLOR_MAP[name]) return COLOR_MAP[name]
  const hex = resolveColor(name, '#3B82F6')
  return { hex, gradient: [hex, hex] }
}

const PyramidVisualization: React.FC<PyramidVisualizationProps> = ({ groups, overallPercentage, compact, lang }) => {
  const t = getUIStrings(lang)
  // Groups come from backend sorted by order (0=base → N=top).
  // For pyramid SVG rendering: index 0 = top (narrowest), so we reverse.
  const pyramidGroups = [...groups].reverse()
  const levelCount = pyramidGroups.length

  const width = 600
  const height = Math.max(400, levelCount * 70 + 100)
  const pyramidWidth = 500
  const pyramidHeight = levelCount * 66
  const startX = (width - pyramidWidth) / 2
  const startY = 50

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Overall Score Card */}
      {!compact && (
        <div className="mb-6 bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl p-4 sm:p-6 shadow-lg">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-1">{t.overallScore}</h3>
              <span className="text-3xl sm:text-4xl font-bold text-primary-600" data-testid="overall-points">
                {formatPoints(totalPoints(groups))}
              </span>
            </div>
            <div className={`text-base sm:text-xl font-bold ${
              overallPercentage >= 80 ? 'text-green-600' :
              overallPercentage >= 60 ? 'text-yellow-600' :
              overallPercentage >= 40 ? 'text-orange-600' :
              'text-red-600'
            }`}>
              {overallPercentage >= 80 ? t.excellent :
               overallPercentage >= 60 ? t.good :
               overallPercentage >= 40 ? t.fair :
               t.needsFocus}
            </div>
          </div>
        </div>
      )}

      {/* SVG Pyramid */}
      <div className={`flex justify-center w-full overflow-hidden ${compact ? '' : 'mb-6'}`}>
        <svg className="w-full h-auto" viewBox={`0 0 ${width} ${height}`}>
          <defs>
            {pyramidGroups.map((group, index) => {
              const c = getColor(group.color)
              return (
                <linearGradient key={`grad-${index}`} id={`pyr-grad-${index}`} x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={c.gradient[0]} />
                  <stop offset="100%" stopColor={c.gradient[1]} />
                </linearGradient>
              )
            })}
          </defs>

          {pyramidGroups.map((group, index) => {
            const levelHeight = pyramidHeight / levelCount
            const topY = startY + index * levelHeight
            const bottomY = topY + levelHeight

            const topWidth = pyramidWidth * (index / levelCount)
            const bottomWidth = pyramidWidth * ((index + 1) / levelCount)
            const topLeftX = startX + (pyramidWidth - topWidth) / 2
            const topRightX = topLeftX + topWidth
            const bottomLeftX = startX + (pyramidWidth - bottomWidth) / 2
            const bottomRightX = bottomLeftX + bottomWidth

            const score = Math.round(group.percentage)
            const opacity = Math.max(0.4, score / 100)

            return (
              <g key={group.group_id}>
                <path
                  d={`M ${topLeftX} ${topY} L ${topRightX} ${topY} L ${bottomRightX} ${bottomY} L ${bottomLeftX} ${bottomY} Z`}
                  fill={`url(#pyr-grad-${index})`}
                  opacity={opacity}
                  stroke="#fff"
                  strokeWidth="2"
                  className="transition-all duration-500 hover:opacity-100"
                />
                <text
                  x={startX + pyramidWidth / 2}
                  y={topY + levelHeight / 2 - 8}
                  textAnchor="middle"
                  className="fill-white font-bold text-sm"
                  style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
                >
                  {group.group_name}
                </text>
                <text
                  x={startX + pyramidWidth / 2}
                  y={topY + levelHeight / 2 + 12}
                  textAnchor="middle"
                  className="fill-white font-bold text-lg"
                  style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
                >
                  {score}%
                </text>
              </g>
            )
          })}
        </svg>
      </div>

      {/* Compact: inline overall score below pyramid */}
      {compact && (
        <div className="text-center mt-2">
          <span className="text-2xl font-bold text-primary-600" data-testid="overall-points">{formatPoints(totalPoints(groups))}</span>
          <span className="ml-2 text-sm text-foreground-muted">{t.points}</span>
        </div>
      )}

      {/* Level Details Grid */}
      {!compact && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {groups.map((group) => {
            const c = getColor(group.color)
            const score = Math.round(group.percentage)
            return (
              <div key={group.group_id} className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: c.hex }} />
                  <h4 className="font-semibold text-gray-800">{group.group_name}</h4>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl font-bold" style={{ color: c.hex }}>{score}%</span>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    score >= 80 ? 'bg-green-100 text-green-700' :
                    score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {score >= 80 ? t.strong : score >= 60 ? t.moderate : t.needsAttention}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${score}%`, backgroundColor: c.hex }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default PyramidVisualization
